import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNav } from "../components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = ["all", "new", "read", "contacted"];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusColor = (status) => {
  if (status === "contacted") return "#1C1A17";
  if (status === "read") return "#6E6A5E";
  return "#7A2E2E"; // new
};

const AdminEnquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const token = typeof window !== "undefined" ? localStorage.getItem("rq_admin_token") : null;

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    axios
      .get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setEnquiries(data))
      .catch(() => {
        localStorage.removeItem("rq_admin_token");
        navigate("/admin");
      })
      .finally(() => setLoading(false));
  }, [navigate, token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/enquiries/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch {
      /* no-op; keep the surface quiet */
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/enquiries/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* no-op */
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return enquiries.filter((e) => {
      const matchesFilter = filter === "all" || (e.status || "new") === filter;
      const matchesQuery =
        !q ||
        e.name.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        (e.message || "").toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [enquiries, query, filter]);

  return (
    <div className="min-h-screen bg-[#F4F1EA] pb-24" data-testid="admin-enquiries-page">
      <AdminNav
        active="enquiries"
        right={
          <button
            onClick={exportCsv}
            className="text-[11px] uppercase tracking-[0.28em] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors"
            data-testid="admin-export-csv"
          >
            Export CSV
          </button>
        }
      />

      <div className="mx-auto max-w-[900px] px-6 md:px-8 pt-14">
        <h1 className="text-[28px] md:text-[32px] italic text-[#1C1A17]">Received in confidence</h1>
        <p className="mt-4 text-[14px] text-[#6E6A5E]" data-testid="admin-enquiry-count">
          {loading
            ? "Reading the archive"
            : `${filtered.length} of ${enquiries.length} ${enquiries.length === 1 ? "enquiry" : "enquiries"}`}
        </p>

        {/* Search + filter */}
        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, or words"
            data-testid="admin-search"
            className="w-full md:max-w-[360px] border-0 border-b border-[#A8A296] bg-transparent py-2 text-[15px] outline-none transition-colors focus:border-[#7A2E2E] text-[#1C1A17] placeholder:text-[#A8A296] placeholder:italic rounded-none"
          />
          <div className="flex gap-5">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`admin-filter-${f}`}
                className={`text-[11px] uppercase tracking-[0.28em] transition-colors ${
                  filter === f ? "text-[#7A2E2E]" : "text-[#A8A296] hover:text-[#1C1A17]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 space-y-10">
          {!loading && filtered.length === 0 && (
            <p className="text-[16px] italic text-[#6E6A5E]" data-testid="admin-empty">
              {enquiries.length === 0 ? "No enquiries have arrived yet." : "Nothing matches that search."}
            </p>
          )}

          {filtered.map((enq) => (
            <div
              key={enq.id}
              className="pb-10 border-b"
              style={{ borderColor: "rgba(168,162,150,0.4)" }}
              data-testid="admin-enquiry-item"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-4">
                  <h2 className="text-[20px] md:text-[22px] italic text-[#1C1A17]">{enq.name}</h2>
                  <span
                    className="text-[10px] uppercase tracking-[0.28em]"
                    style={{ color: statusColor(enq.status) }}
                    data-testid="admin-enquiry-status"
                  >
                    {enq.status || "new"}
                  </span>
                </div>
                <span className="text-[12px] text-[#6E6A5E]">{formatDate(enq.created_at)}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-[14px] text-[#6E6A5E]">
                <a href={`mailto:${enq.email}`} className="hover:text-[#7A2E2E] transition-colors">
                  {enq.email}
                </a>
                {enq.telephone ? <span>{enq.telephone}</span> : null}
              </div>
              <p className="mt-5 text-[16px] leading-[1.8] text-[#1C1A17] whitespace-pre-wrap">
                {enq.message}
              </p>
              <div className="mt-6 flex gap-6">
                <button
                  onClick={() => updateStatus(enq.id, "read")}
                  data-testid="admin-mark-read"
                  className="text-[10px] uppercase tracking-[0.28em] text-[#6E6A5E] hover:text-[#1C1A17] transition-colors"
                >
                  Mark read
                </button>
                <button
                  onClick={() => updateStatus(enq.id, "contacted")}
                  data-testid="admin-mark-contacted"
                  className="text-[10px] uppercase tracking-[0.28em] text-[#6E6A5E] hover:text-[#1C1A17] transition-colors"
                >
                  Mark contacted
                </button>
                {(enq.status && enq.status !== "new") && (
                  <button
                    onClick={() => updateStatus(enq.id, "new")}
                    data-testid="admin-mark-new"
                    className="text-[10px] uppercase tracking-[0.28em] text-[#6E6A5E] hover:text-[#7A2E2E] transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminEnquiries;
